(() => {
  const enhanced = new WeakSet();
  const animations = new WeakMap();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 1200px)');
  const searchObservers = new Map();
  function syncSearchState() {
    let active = false;
    for (const [search, observer] of searchObservers) {
      if (!search.isConnected) {
        observer.disconnect();
        searchObservers.delete(search);
      } else if (search.hasAttribute('open') || search.style.display === 'block') {
        active = true;
      }
    }
    document.body?.classList.toggle('mn-search-active', active);
  }
  function trackSearch(root = document) {
    const searches = root.matches?.('header-search') ? [root] : root.querySelectorAll('header-search');
    for (const search of searches) {
      if (searchObservers.has(search)) continue;
      const observer = new MutationObserver(syncSearchState);
      observer.observe(search, { attributes: true, attributeFilter: ['open', 'style'] });
      searchObservers.set(search, observer);
    }
    syncSearchState();
  }
  document.addEventListener('dialog:before-show', event => {
    if (!event.target.matches?.('header-search')) return;
    trackSearch(event.target);
    document.body?.classList.add('mn-search-active');
  }, true);
  document.addEventListener('dialog:after-hide', event => {
    if (event.target.matches?.('header-search')) syncSearchState();
  }, true);
  window.addEventListener('pageshow', () => trackSearch());
  document.addEventListener('shopify:section:unload', event => {
    for (const [search, observer] of searchObservers) {
      if (event.target !== search && !event.target.contains(search)) continue;
      observer.disconnect();
      searchObservers.delete(search);
    }
    syncSearchState();
  });
  trackSearch();
  function motion(disclosure, entering) {
    const oldAnimation = animations.get(disclosure);
    const content = disclosure.contentElement;
    const interrupted = oldAnimation && ['running', 'pending'].includes(oldAnimation.playState);
    const current = interrupted ? getComputedStyle(content) : null;
    const startOpacity = current?.opacity;
    const startTransform = current?.transform;
    if (oldAnimation) oldAnimation.cancel();
    const frames = reduced.matches
      ? { opacity: entering ? 1 : 0, transform: 'none' }
      : { opacity: [startOpacity ?? (entering ? 0 : 1), entering ? 1 : 0], transform: [startTransform ?? (entering ? 'translateY(6px)' : 'translateY(0)'), entering ? 'translateY(0)' : 'translateY(-4px)'] };
    const animation = content.animate(frames, { duration: reduced.matches ? 1 : entering ? 240 : 160, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both' });
    animations.set(disclosure, animation);
    return { finished: animation.finished.catch(() => undefined), finish: () => animation.finish() };
  }
  function setup(root = document) {
    const header = root.matches?.('.mn-header') ? root : root.querySelector('.mn-header');
    if (!header || enhanced.has(header)) return;
    enhanced.add(header);
    for (const disclosure of header.querySelectorAll('.header__menu-disclosure')) {
      disclosure.createShowAnimationControls = () => motion(disclosure, true);
      disclosure.createHideAnimationControls = () => motion(disclosure, false);
    }
    header.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const disclosure = event.target.closest('.header__menu-disclosure[open]');
      if (!disclosure) return;
      event.preventDefault();
      event.stopPropagation();
      disclosure.toggle(false);
      disclosure.summaryElement.focus();
    });
  }
  function isVisibleFocusTarget(element) {
    return element?.isConnected && element.getClientRects().length > 0 && !['hidden', 'collapse'].includes(getComputedStyle(element).visibility);
  }
  function adaptNavigation() {
    for (const header of document.querySelectorAll('.mn-header')) {
      if (desktop.matches && header.dataset.layout !== 'drawer') {
        const sidebar = document.querySelector('header-sidebar.mn-header-sidebar[open]');
        if (sidebar) {
          const focused = document.activeElement;
          const recoverFocus = sidebar.contains(focused) || focused === header.querySelector('.mn-header__menu-toggle');
          sidebar.hide().then(() => {
            if (!recoverFocus || !desktop.matches || !header.isConnected || !sidebar.isConnected || sidebar.open || header.dataset.layout === 'drawer') return;
            const currentFocus = document.activeElement;
            if (currentFocus && currentFocus !== document.body && isVisibleFocusTarget(currentFocus)) return;
            const logo = header.querySelector('.header__logo[href], .header__logo a[href]');
            if (isVisibleFocusTarget(logo)) logo.focus({ preventScroll: true });
          }, () => {});
        }
      } else {
        const focused = document.activeElement;
        for (const disclosure of header.querySelectorAll('.header__menu-disclosure[open]')) disclosure.toggle(false, false);
        if (document.activeElement === focused && header.contains(focused) && !isVisibleFocusTarget(focused)) {
          const toggle = header.querySelector('.mn-header__menu-toggle');
          if (isVisibleFocusTarget(toggle)) toggle.focus({ preventScroll: true });
        }
      }
    }
  }
  Promise.all([customElements.whenDefined('mega-menu-disclosure'), customElements.whenDefined('dropdown-menu-disclosure')]).then(() => setup());
  desktop.addEventListener('change', adaptNavigation);
  reduced.addEventListener('change', () => {
    if (!reduced.matches) return;
    for (const disclosure of document.querySelectorAll('.mn-header .header__menu-disclosure')) {
      const animation = animations.get(disclosure);
      if (animation?.playState === 'running' || animation?.playState === 'pending') animation.finish();
    }
  });
  document.addEventListener('shopify:section:load', event => {
    setup(event.target);
    trackSearch(event.target);
  });
})();

